import WaveSurfer, { WaveSurferOptions } from 'wavesurfer.js';
import Hls from 'hls.js';

const baseWaveSurferConfig = {
  container: '',
  height: 64,
  barRadius: 0,
  cursorWidth: 0,
  cursorColor: 'transparent',
  normalize: true,
  progressColor: '#FF5B27',
  waveColor: '#404040',
  fillParent: true,
  mediaControls: false,
  autoplay: false,
  interact: true,
  dragToSeek: true,
  sampleRate: 48000,
};

export interface IAudioPlayer {
  audio: HTMLAudioElement;
  sourceUrl: string | undefined;
  wavesurfer: WaveSurfer | null;
  playPauseCallback: (() => void) | null;
  audioProcessCallback: ((currentTime: number) => void) | null;
  hls: Hls | null;
  waveSurferConfig: WaveSurferOptions;
  container: HTMLElement;
  play(): void;
  pause(): void;
  stop(): void;
  seek(time: number): void;
  getCurrentTime(): number;
  getDuration(): number;
  isPlaying(): boolean;
  destroy(): void;
  setPlayPauseCallback(callback: () => void): void;
  setAudioProcessCallback(callback: (currentTime: number) => void): void;
  initWaveSurfer(): void;
  updateZoom(minPxPerSec: number): void;
  setReadyCallback(callback: () => void): void;
}

export class AudioPlayer implements IAudioPlayer {
  public audio: HTMLAudioElement;
  public sourceUrl: string | undefined;
  public wavesurfer: WaveSurfer | null = null;
  public playPauseCallback: (() => void) | null = null;
  public audioProcessCallback: ((currentTime: number) => void) | null = null;
  public hls: Hls | null = null;
  public waveSurferConfig: WaveSurferOptions = baseWaveSurferConfig;
  public container: HTMLElement;
  public readyCallback: (() => void) | null = null;

  constructor(sourceUrl: string | undefined, container: HTMLElement, waveSurferConfig: WaveSurferOptions = baseWaveSurferConfig) {
    this.sourceUrl = sourceUrl;
    this.audio = new Audio(this.sourceUrl);
    this.audio.addEventListener('ended', () => {
      this.reset();
    });
    this.waveSurferConfig = {
      ...waveSurferConfig,
    };
    this.container = container;
    this.initWaveSurfer();
  }

  async initWaveSurfer() {
    const url = this.sourceUrl;
    if (this.wavesurfer) {
      this.wavesurfer.destroy();
    }
    this.wavesurfer = WaveSurfer.create({
      ...this.waveSurferConfig,
      container: this.container,
      url: url?.endsWith('.m3u8') ? undefined : url,
      width: this.container.clientWidth,
    });

    if (url?.endsWith('.m3u8') && Hls.isSupported()) {
      await this.initHls(url);
    }

    this.wavesurfer.on('ready', () => {
      console.log('wavesurfer on ready');
      this.readyCallback?.();
    });

    this.wavesurfer.on('error', (error) => {
      console.log('wavesurfer on error', error);
      console.error('WaveSurfer error:', error);
    });

    this.wavesurfer.on('play', () => {
      this.playPauseCallback?.();
    });

    this.wavesurfer.on('pause', () => {
      this.playPauseCallback?.();
    });

    this.wavesurfer.on('audioprocess', () => {
      this.audioProcessCallback?.(this.getCurrentTime());
    });
    console.time('end');
  }

  protected async initHls(url: string): Promise<void> {
    const mediaElement = this.wavesurfer?.getMediaElement();
    if (!mediaElement) return;

    this.hls = new Hls({
      maxBufferLength: 120,
    });

    this.hls.loadSource(url);
    this.hls.attachMedia(mediaElement);

    return new Promise<void>((resolve, reject) => {
      this.hls?.on(Hls.Events.FRAG_LOADED, () => {
        resolve();
      });

      this.hls?.on(Hls.Events.ERROR, (_, data) => {
        if (data?.buffer === 0 && this.hls?.media) {
          const currentTime = this.hls.media.currentTime || 0;
          this.hls.media.currentTime = currentTime + 10;
        }
        reject(new Error(`HLS Error: ${data}`));
      });
    });
  }

  protected async checkAudioPlayability(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('audio')) {
        throw new Error('The provided URL does not point to an audio file');
      }
    } catch (error) {
      return false;
    }
    return true;
  }

  public play(): void {
    this.wavesurfer?.play();
  }

  public pause(): void {
    this.wavesurfer?.pause();
  }

  public stop(): void {
    this.wavesurfer?.stop();
    this.reset();
  }

  public seek(time: number): void {
    this.wavesurfer?.seekTo(time / this.getDuration());
  }

  protected reset(): void {
    this.wavesurfer?.seekTo(0);
  }

  public getCurrentTime(): number {
    return this.wavesurfer?.getCurrentTime() || 0;
  }

  public getDuration(): number {
    return this.wavesurfer?.getDuration() || 0;
  }

  public isPlaying(): boolean {
    return this.wavesurfer?.isPlaying() || false;
  }

  public destroy(): void {
    this.hls?.destroy();
    this.wavesurfer?.destroy();
  }

  public setPlayPauseCallback(callback: () => void): void {
    this.playPauseCallback = callback;
  }

  public setAudioProcessCallback(callback: (currentTime: number) => void): void {
    this.audioProcessCallback = callback;
  }

  public updateZoom(minPxPerSec: number): void {
    if (!this.wavesurfer) return;
    this.wavesurfer.zoom(minPxPerSec);
  }

  public setReadyCallback(callback: () => void): void {
    this.readyCallback = callback;
  }
}
